import { useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import loginRequest, { LoginVariables, Login } from 'data/requests/logIn';
import logoutRequest from 'data/requests/logOut';
import { FirebaseAuthContext } from 'data/firebase';
import { setToken, removeToken } from 'utils/storage';
import { CDError } from 'utils/error';

const useAuth = () => {
  const firebaseAuth = useContext(FirebaseAuthContext);
  const queryCache = useQueryClient();
  const { mutateAsync: loginMut, isLoading: loginLoading } = useMutation<
    Login,
    CDError,
    LoginVariables
  >(loginRequest);
  const { mutateAsync: logoutMut, isLoading: logoutLoading } = useMutation<
    any,
    CDError
  >(logoutRequest);

  const login = useCallback(
    async ({ username, password }: LoginVariables) => {
      const response = await loginMut({
        username,
        password,
      });
      if (response) {
        const tokens = JSON.stringify({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          externalTokens: response.externalTokens,
        });
        setToken(tokens);
        queryCache.refetchQueries('me');
      }
      return response;
    },
    [loginMut, queryCache],
  );

  const logout = useCallback(async () => {
    const response = await logoutMut();
    removeToken();
    firebaseAuth.signOut();
    queryCache.setQueryData('me', null);
    queryCache.clear();
    return response;
  }, [logoutMut, queryCache, firebaseAuth]);

  return {
    login,
    logout,
    loginLoading,
    logoutLoading,
  };
};

export default useAuth;
